import { HttpClient, HttpEventType, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { NGXLogger } from 'ngx-logger';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { FileResponse } from '../models/fileResponse';
import { MetricAction, MetricActionType, MetricModule, MetricType, Metrics } from '../models/metrics';
import { Progress } from '../models/progress.model';
import {
  ApproveRejectStockTransferRequest,
  StockTransfer,
  StockTransferRequestListResponse,
  StockTransferRequestListSearchCriteria
} from '../models/stock-transfer-request.model';
import { BaseService } from './base.service';

type UploadResultType = Progress | FileResponse | null | any;

@Injectable()
export class StockTransferRequestService extends BaseService {
  public headers: HttpHeaders;

  constructor(
    private readonly http: HttpClient,
    protected readonly logger: NGXLogger
  ) {
    super();
    this.envService = this.env.services.stockTransferRequest;
    this.headers = new HttpHeaders(this.envService.headers);
  }

  public searchByCriteria(
    criteria: StockTransferRequestListSearchCriteria
  ): Observable<StockTransferRequestListResponse> {
    const metricsReq: Metrics = {
      metrics: [
        {
          metricType: MetricType.RESPONSE_TIME,
          module: MetricModule.STOCK_TRANSFER,
          route: this.envService.list,
          action: MetricActionType.SEARCH
        }
      ]
    };
    const params = this.getParams(criteria);
    const url = this.getFullUrl(this.envService.list);
    return this.http.get<StockTransferRequestListResponse>(url, {
      headers: this.loaderHeaders(null, metricsReq),
      observe: 'body',
      params
    });
  }

  public save(stockTransfer: StockTransfer): Observable<StockTransfer> {
    if (stockTransfer.id) {
      return this.http.put<StockTransfer>(this.getFullUrl(this.envService.update), stockTransfer, {
        headers: this.loaderHeaders(),
        observe: 'body'
      });
    } else {
      return this.http.post<StockTransfer>(this.getFullUrl(this.envService.create), stockTransfer, {
        headers: this.loaderHeaders(),
        observe: 'body'
      });
    }
  }

  public onExportTemplate() {
    const url = this.getFullUrl(this.envService.exportTemplate);

    return this.http.get<any>(url, {
      headers: this.loaderHeaders(),
      observe: 'body',
      responseType: 'blob' as 'json'
    });
  }

  public getStockTransferRequestById(id: string): Observable<StockTransfer> {
    const url = this.getFullUrl(this.envService.get, { id });

    return this.http.get<any>(url, {
      headers: this.headers,
      observe: 'body'
    });
  }

  public getStockTransferRequestByDocNo(docNo: string): Observable<StockTransfer> {
    const url = this.getFullUrl(this.envService.getByDocNo, { docNo });

    return this.http.get<any>(url, {
      headers: this.headers,
      observe: 'body'
    });
  }

  public deleteStockTransferRequest(id: string): Observable<any> {
    const url = this.getFullUrl(this.envService.delete, { id });

    return this.http.delete<any>(url, {
      headers: this.loaderHeaders(3000),
      observe: 'body'
    });
  }

  public cancelStockTransferRequest(data: ApproveRejectStockTransferRequest): Observable<any> {
    return this.http.post<any>(this.getFullUrl(this.envService.cancel), data, {
      headers: this.loaderHeaders(3000),
      observe: 'body'
    });
  }

  public approve(data: ApproveRejectStockTransferRequest): Observable<any> {
    const url = this.getFullUrl(this.envService.approval);
    return this.http.post<any>(url, data, {
      headers: this.loaderHeaders(3000),
      observe: 'body'
    });
  }

  public reject(data: ApproveRejectStockTransferRequest): Observable<any> {
    const url = this.getFullUrl(this.envService.approval);
    return this.http.post<any>(url, data, {
      headers: this.loaderHeaders(3000),
      observe: 'body'
    });
  }

  public importItems(files: Array<File>, wmsCode: string): Array<Observable<UploadResultType>> {
    return files.map(file => this.importItem(file, wmsCode));
  }

  public importItem(file: File, wmsCode: string): Observable<UploadResultType> {
    const url = this.getFullUrl(this.envService.import);
    const formData = new FormData();

    formData.append('file', file, file.name);
    formData.append('wmsCode', wmsCode);

    this.headers.append('Content-Type', 'multipart/form-data');
    const metricsReq: Metrics = {
      metrics: [
        {
          metricType: MetricType.COUNTER,
          module: MetricModule.STOCK_TRANSFER,
          path: `${this.envService.import}`
        },
        {
          metricType: MetricType.RESPONSE_TIME,
          module: MetricModule.STOCK_TRANSFER,
          route: `${this.envService.import}`,
          action: MetricAction.IMPORT
        }
      ]
    };

    return this.http
      .post<any>(url, formData, {
        reportProgress: true,
        observe: 'events',
        headers: this.loaderHeaders(0, metricsReq)
      })
      .pipe(
        map(event => {
          switch (event.type) {
            case HttpEventType.UploadProgress:
              const progress = Math.round((100 * event.loaded) / event.total);
              return { status: 'progress', message: progress };
            case HttpEventType.Response:
              return { ...event.body, status: 'done' };
            default:
              const msg = `Unhandled event: ${HttpEventType[event.type]}`;
              this.logger.info(msg);
              return null;
          }
        }),
        catchError(err => {
          return of(err);
        })
      );
  }

  public validateStockTransferRequest(stockTransfer: StockTransfer): Observable<any> {
    const url = this.getFullUrl(this.envService.validate);
    return this.http.post<any>(url, stockTransfer, {
      headers: this.loaderHeaders(),
      observe: 'body'
    });
  }

  public submit(stockTransfer: StockTransfer): Observable<any> {
    const url = this.getFullUrl(this.envService.submit);
    return this.http.post<any>(url, stockTransfer, {
      headers: this.loaderHeaders(3000),
      observe: 'body'
    });
  }

  getHistoryLogs(payload: string) {
    const url = this.getFullUrl(this.envService.history, {
      id: payload
    });
    return this.http.get<any>(url, { headers: this.headers, observe: 'body' });
  }
}
